import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

export const Hero = () => {
    const { t } = useLanguage();
    const reduce = useReducedMotion();
    const fade = (delay) => ({
        initial: reduce ? false : { opacity: 0, y: 24 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
    });

    return (
        <section id="top" data-testid="hero-section" className="noise-overlay relative overflow-hidden bg-nx-base">
            <div className="bg-grid-fine absolute inset-0" aria-hidden="true" style={{ maskImage: "radial-gradient(ellipse 90% 70% at 50% 30%, black 30%, transparent 75%)", WebkitMaskImage: "radial-gradient(ellipse 90% 70% at 50% 30%, black 30%, transparent 75%)" }} />
            <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-[56rem] -translate-x-1/2 rounded-full bg-nx-blue/[0.06] blur-[120px]" aria-hidden="true" />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-5 pb-14 pt-28 sm:px-6 sm:pt-32 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-20 lg:pt-36">
                <div className="lg:col-span-6">
                    <motion.div {...fade(0)} className="flex items-center gap-2.5" data-testid="hero-eyebrow">
                        <span className="relative flex h-1.5 w-1.5">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-nx-lime opacity-60" />
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-nx-lime" />
                        </span>
                        <span className="h-1.5 w-1.5 rounded-full bg-nx-blue" aria-hidden="true" />
                        <span className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.24em] text-nx-sub">
                            {t("Global Web3 • DeFi Ecosystem")}
                        </span>
                    </motion.div>

                    <motion.h1
                        {...fade(0.1)}
                        className="mt-6 text-[2.55rem] leading-[1.04] sm:text-6xl lg:text-[4.4rem] font-extrabold tracking-tight text-nx-text"
                    >
                        {t("The Next Generation of")}{" "}
                        <span className="text-nx-lime">{t("Decentralized")}</span> {t("Finance.")}
                    </motion.h1>

                    <motion.p {...fade(0.2)} className="mt-6 max-w-xl text-base lg:text-lg leading-relaxed text-nx-sub" data-testid="hero-description">
                        {t("A global smart-contract ecosystem built around decentralized technology, structured distribution and the future of Web3.")}
                    </motion.p>

                    <motion.div {...fade(0.3)} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                        <a
                            href="#join"
                            data-testid="hero-explore-button"
                            className="btn-sweep group inline-flex items-center justify-center gap-2 rounded-full bg-nx-lime px-7 py-3.5 text-sm font-extrabold tracking-wide text-nx-base shadow-[0_6px_32px_rgba(184,255,24,0.22)] transition-all duration-300 hover:brightness-110 hover:-translate-y-px"
                        >
                            {t("Explore Nexora")}
                            <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-0.5 rtl:rotate-180" aria-hidden="true" />
                        </a>
                        <a
                            href="#ecosystem"
                            data-testid="hero-ecosystem-button"
                            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-7 py-3.5 text-sm font-bold tracking-wide text-nx-text backdrop-blur-sm transition-all duration-300 hover:bg-white/[0.08] hover:-translate-y-px"
                        >
                            {t("View Ecosystem")}
                            <ArrowUpRight size={16} strokeWidth={2} className="text-nx-sub transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    initial={reduce ? false : { opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    className="relative lg:col-span-6"
                    data-testid="hero-visual"
                >
                    <div className="relative mx-auto max-w-[440px] sm:max-w-[500px]">
                        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[72%] w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-nx-lime/[0.07] blur-[100px]" aria-hidden="true" />
                        <div className="pointer-events-none absolute -right-6 top-1/4 h-1/2 w-1/3 rounded-full bg-nx-blue/[0.09] blur-[90px] rtl:-left-6 rtl:right-auto" aria-hidden="true" />
                        <motion.img
                            src="/assets/nexora-hand.png"
                            alt="Nexora Global emblem held in hand"
                            className="relative w-full [mask-image:radial-gradient(ellipse_70%_70%_at_50%_47%,black_55%,transparent_95%)]"
                            animate={reduce ? undefined : { y: [0, -10, 0] }}
                            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                            loading="eager"
                        />
                        <div className="bg-grid-fine pointer-events-none absolute inset-0 opacity-40" aria-hidden="true" style={{ maskImage: "radial-gradient(ellipse 65% 60% at 50% 45%, black 20%, transparent 70%)", WebkitMaskImage: "radial-gradient(ellipse 65% 60% at 50% 45%, black 20%, transparent 70%)" }} />
                        <div className="pointer-events-none absolute inset-x-8 bottom-0 h-24 bg-gradient-to-t from-nx-base via-nx-base/60 to-transparent" aria-hidden="true" />
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
